import React, { Component } from 'react';
import { observable, action, makeObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type { OllamaStreamResponse, Model, TranslationResult, LoadedModel, ExplainRequest, ExplanationResult } from '../models.ts';
import { PromiseCompletion } from '../classes/PromiseCompletion.ts';
import { helperService } from '../services/helperService.ts';
import apiService from '../services/apiService.ts';
import textService from '../services/textService.tsx';
import { imageService } from '../services/imageService.ts';

import { Modals, modalService } from '../components/Modal.tsx';

import { ImgCopy } from '../components/Icons/ImgCopy.tsx';
import { ImgAttach } from '../components/Icons/ImgAttach.tsx';
import { ImgSwap } from '../components/Icons/ImgSwap.tsx';
import { ImgTrash } from '../components/Icons/ImgTrash.tsx';
import { ImgRetry } from '../components/Icons/ImgRetry.tsx';

import ContextMenu, { ContextMenuItem } from '../components/ContextMenu/index.ts';

import ModelSelector from '../components/ModelSelector.tsx';
import ThemeSelector from '../components/ThemeSelector.tsx';
import LanguageSelector from '../components/LanguageSelector.tsx';
import { ThinkingIndicator } from '../components/ThinkingIndicator.tsx';
import { ImageWrapper } from '../components/ImageWrapper.tsx';
import MicToText from '../components/MicToText.tsx';
import { ImgQuestion } from '../components/Icons/ImgQuestion.tsx';
import { ImgOllama } from '../components/Icons/ImgOllama.tsx';
import { ImgTranslate } from '../components/Icons/ImgTranslate.tsx';

const defaultUserLanguage = navigator.language || 'en';

const KeyWordItem: React.FC<{ word: string; translation: string; onExplain?: (selection: string) => void }> = ({ word, translation, onExplain }) => {
  const wordRef = React.useRef<HTMLElement>(null);
  const translationRef = React.useRef<HTMLElement>(null);
  const [wordState, setWordState] = React.useState<'idle' | 'success' | 'error'>('idle');
  const [transState, setTransState] = React.useState<'idle' | 'success' | 'error'>('idle');

  const copyText = async (text: string, setState: (s: 'idle' | 'success' | 'error') => void) => {
    try {
      await navigator.clipboard.writeText(text);
      setState('success');
    } catch {
      setState('error');
    }
    setTimeout(() => setState('idle'), 1500);
  };

  return (
    <li>
      <strong ref={wordRef} className={`keyword-item${wordState !== 'idle' ? ` keyword-copy-${wordState}` : ''}`}>
        {word}
      </strong>
      <ContextMenu container={wordRef} showOnClick>
        {() => <>
          <ContextMenuItem title="Copy word" onClick={() => copyText(word, setWordState)} />
          {onExplain && <ContextMenuItem title="Explain" onClick={() => onExplain(word)} />}
        </>}
      </ContextMenu>
      {': '}
      <span ref={translationRef} className={`keyword-item${transState !== 'idle' ? ` keyword-copy-${transState}` : ''}`}>
        {translation}
      </span>
      <ContextMenu container={translationRef} showOnClick>
        {() => <>
          <ContextMenuItem title="Copy translation" onClick={() => copyText(translation, setTransState)} />
          {onExplain && <ContextMenuItem title="Explain" onClick={() => onExplain(word)} />}
        </>}
      </ContextMenu>
    </li>
  );
};

@observer
class Translate extends Component<unknown> {
  @observable _currentModel: Model | null  = null;
  @observable _abortControllers: Map<string, AbortController> = new Map();
  @observable _languageFrom = localStorage.getItem('languageFrom') || navigator.languages.filter(lang => lang !== defaultUserLanguage && lang.length == 2)[0] || 'en';
  @observable _languageTo = localStorage.getItem('languageTo') || defaultUserLanguage;
  @observable _textFrom = localStorage.getItem('textFrom') || '';
  @observable _translationResult: TranslationResult | null = null;
  @observable _selectedText = '';
  @observable _explanationResult: ExplanationResult | null = null;
  @observable.ref _textAreaFromRef = React.createRef<HTMLTextAreaElement>();
  _loading = new PromiseCompletion();
  _modelSelectorRef = React.createRef<ModelSelector>();
  @observable _fileRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable _imageBase64: string | null = localStorage.getItem('image');

  constructor(props: unknown) {
    super(props);
    makeObservable(this);
  }

  @action
  componentDidMount() {
    document.getElementsByClassName('ui-loader-container')[0]?.remove();
    const translationSaved = localStorage.getItem('translation');
    if (translationSaved) {
      try {
        this._translationResult = JSON.parse(translationSaved) as TranslationResult;
        this._textFrom = this._translationResult.input ?? '';
      } catch (e) {
        console.error('Failed to parse saved translation:', e);
      }
    }
  }

  @action 
  _onModelChange = (model: Model | null) => {
    this._currentModel = model;
    if (model) {
      localStorage.setItem('currentModel', `${model.server}@${model.name}`);
      this._doTranslate();
    }
  };

  render() {
    const hasVision = this._currentModel?.capabilities.includes('vision');
    const isModelAvailable = this._currentModel !== null;

    const hasSynonyms = !!this._translationResult?.synonyms;
    const hasComplicatedWords = !!this._translationResult?.mostComplicatedWords?.length;
    const hasComments = !!this._translationResult?.comments;
    const hasExplanation = !!this._explanationResult;
    const hasHelp = hasSynonyms || hasComplicatedWords || hasComments || hasExplanation;

    const textFrom = this._textFrom ?? '';
    const isInputSmall = textFrom.length < 100 && textFrom.split('\n').length < 3;

    const isThinking = this._translationResult && !this._translationResult.isDone && !this._translationResult?.translation && !this._translationResult.errorMessage;

    return (
      <>
        <header className="header">
          <div className="container">
            <div className="header-content">
              <div className="logo">
                <ImgOllama />
                <ImgTranslate />
                <span>AI Translator</span>
              </div>
              <div className="header-controls">
                <ModelSelector ref={this._modelSelectorRef} onModelChange={this._onModelChange} model={this._currentModel} />
                {/*<div className="model-selector">
                  <div className="model-dropdown-selected" id="modelDropdownSelected">Llama</div>
                  <div className="model-dropdown-options" id="modelDropdownOptions">
                    <div className="model-dropdown-option" data-value="gemma3">Gemma 3</div>
                    <div className="model-dropdown-option selected" data-value="llama">Llama</div>
                    <div className="model-dropdown-option" data-value="gpt">GPT</div>
                    <div className="model-dropdown-option" data-value="claude">Claude</div>
                  </div>
                </div>*/}
                <ThemeSelector />
              </div>
            </div>
          </div>
        </header>

        {/*<div className="recent-languages">
          <div className="container">
            <div className="recent-tabs" id="recentTabs">
              <div className="recent-tab active" data-pair="en-pl">
                English → Polish
              </div>
            
              <div className="recent-tab " data-pair="pl-en">
                Polish → English
              </div>
            
              <div className="recent-tab " data-pair="en-en">
                English → English
              </div>
            </div>
          </div>
        </div>*/}

        <main className="main-content">
          <div className="container">
            <div className="translator-container">
              {/* <!-- Source Panel --> */}
              <div className="translation-panel">
                <div className="panel-header">
                  <LanguageSelector languageCode={this._languageFrom} onLanguageChange={this._onLanguageFromChange} />
                  <div className="panel-actions">
                    {isModelAvailable && <MicToText language={this._languageFrom} onTranscription={this._onTranscription} />}
                    {(!!textFrom || !!this._imageBase64) && <button className="panel-btn" title="Clear text" onClick={this._onClearClick}>
                      <ImgTrash />
                    </button>}
                    {hasVision && <button className="panel-btn" title="Attach image" onClick={this._onAttachClick}>
                      <ImgAttach />
                    </button>}
                    {!!textFrom && <button className="panel-btn" title="Copy text" onClick={this._onCopySourceClick}>
                      <ImgCopy />
                    </button>}
                    {!!this._selectedText && isModelAvailable && <button className="panel-btn explain-btn" title="Explain selection" onClick={this._onExplainClick}>
                      <ImgQuestion size={18} />
                    </button>}
                  </div>
                </div>
                <div className="panel-content">
                  
                  {!this._imageBase64 && <textarea 
                    ref={this._textAreaFromRef} 
                    className={`text-area ${isInputSmall ? 'text-area-large' : ''}`}  
                    placeholder="Enter text to translate..." 
                    value={textFrom} 
                    onChange={this._onTextFromChange}                    
                    onPaste={hasVision ? this._onPaste : undefined}
                    onSelect={this._onTextSelect}
                    onMouseUp={this._onTextSelect}
                    onKeyUp={this._onTextSelect}
                    ></textarea>}
                  {this._imageBase64 && (
                    <div className="image-preview-container">
                      <ImageWrapper src={`data:${imageService.detectImageTypeFast(this._imageBase64)};base64,${this._imageBase64}`} alt="Uploaded" className="image-preview"/>
                    </div>
                  )}
                  <input type="file" className="file-input" accept="image/*" ref={this._fileRef} onChange={this._onImageUploadChange}/>
                  <div className="image-drop-zone" id="dropZone">
                    <div className="drop-text">Drop image here to translate</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>or click to browse</div>
                  </div>
                  {this._translationResult 
                    && this._translationResult.isDone 
                    && this._translationResult?.correctedInput 
                    && textFrom.trim() !== this._translationResult.correctedInput.trim() 
                    && this._translationResult.input.trim() === textFrom.trim()
                    && (
                    <div className="correction-suggestion">
                      <span>Did you mean:</span> <a onClick={this._applyCorrection}>{textService.highlightDifferencesJSX(this._translationResult.input, this._translationResult.correctedInput)}</a>
                    </div>
                  )}
                </div>                
              </div>

              {/* <!-- Swap Button --> */}
              <div className="swap-container">
                <button className="swap-btn" id="swapBtn" title="Swap languages" onClick={this._onSwapClick}>
                  <ImgSwap />
                </button>
              </div>

              {/* <!-- Target Panel --> */}
              <div className="translation-panel">
                <div className="panel-header">
                  <LanguageSelector languageCode={this._languageTo} onLanguageChange={this._onLanguageToChange} />
                  <div className="panel-actions">
                    {this._translationResult?.isDone && <button className="panel-btn" title="Retry translation" onClick={this._callOllamaApi}>
                      <ImgRetry />
                    </button>}
                    {!!this._translationResult?.translation && <button className="panel-btn" title="Copy translation" onClick={this._onCopyTargetClick}>
                      <ImgCopy />
                    </button>}
                  </div>
                </div>
                <div className="panel-content">
                  {!isThinking && <textarea className={`text-area ${isInputSmall ? 'text-area-large' : ''}`} placeholder="Translation will appear here..." readOnly value={this._translationResult?.translation}></textarea>}
                  {isThinking && (
                    <div className="thinking-indicator-container">
                      <ThinkingIndicator text={`Thinking${this._translationResult?.thinkingTokenCount ? ` (${this._translationResult.thinkingTokenCount})` : ''}`} />
                      {!this._translationResult?.thinkingTokenCount && this._renderLoadingProgress()}
                    </div>
                  )}
                  {this._translationResult?.errorMessage && (
                    <div className="translation-error-message">
                      <strong>Error:</strong> {this._translationResult.errorMessage}
                    </div>
                  )}
                </div>
              </div>

              {/* <!-- Separator --> */}
              <div className="separator"></div>

              {/* <!-- Help Panel --> */}
              {hasHelp && <div className="help-panel-container">

                {hasExplanation && <div className="help-section">
                  <div className="help-title">
                    <ImgQuestion size={14} />
                    Explanation{this._explanationResult?.selection ? `: "${this._explanationResult.selection}"` : ''}
                  </div>
                  <div className="help-text explanation-text">
                    {!this._explanationResult?.isDone && !this._explanationResult?.explanation && !this._explanationResult?.errorMessage && (
                      <div className="thinking-indicator-container"><ThinkingIndicator text={`Explaining${this._explanationResult?.tokenCount ? ` (${this._explanationResult.tokenCount})` : ''}`} /></div>
                    )}
                    {this._explanationResult?.explanation && (
                      <div className="help-text">{this._explanationResult.explanation}</div>
                    )}
                    {this._explanationResult?.examples && (
                      <div className="help-text"><strong>Examples:</strong> {this._explanationResult.examples}</div>
                    )}
                    {this._explanationResult?.comments && (
                      <div className="help-text explanation-text">{this._explanationResult.comments}</div>
                    )}
                    {this._explanationResult?.errorMessage && (
                      <div className="translation-error-message"><strong>Error:</strong> {this._explanationResult.errorMessage}</div>
                    )}
                  </div>
                </div>}
                
                {hasSynonyms && <div className="help-section">
                  <div className="help-title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Alternatives &amp; Synonyms
                  </div>
                  <ul className="alternatives-list" id="alternativesList"><li>{this._translationResult?.synonyms}</li></ul>
                </div>}

                {hasComplicatedWords && <div className="help-section">
                  <div className="help-title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M8 14s1.5 2 4 2 4-2 4-2"></path>
                      <line x1="9" y1="9" x2="9.01" y2="9"></line>
                      <line x1="15" y1="9" x2="15.01" y2="9"></line>
                    </svg>
                    Key words
                  </div>
                  <div className="help-text">
                    <ul>
                      {this._translationResult?.mostComplicatedWords?.map((word, index) => (
                        <KeyWordItem key={index} word={word.word} translation={word.translation} onExplain={isModelAvailable ? this._onExplainKeyword : undefined} />
                      ))}
                    </ul>
                  </div>
                </div>}

                {hasComments && <div className="help-section">
                  <div className="help-title">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14,2 14,8 20,8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10,9 9,9 8,9"></polyline>
                    </svg>
                    Quick Comment
                  </div>
                  <div className="help-text">{this._translationResult?.comments}</div>
                </div>}
                    
              </div>}
            </div>
          </div>
        </main>

        <footer className="footer">
          <div className="container">
            <div className="copyright">
              © {new Date().getFullYear()} AI Translator. All rights reserved.
            </div>
          </div>
        </footer>

        
        <Modals />
      </>
    );
  }

  @action 
  _onLanguageFromChange = (language: string) => {
    const previousLanguage = this._languageFrom;
    this._languageFrom = language;
    localStorage.setItem('languageFrom', language);

    if (this._languageFrom === this._languageTo) {
      this._onLanguageToChange(previousLanguage);
    }

    this._doTranslate();
  };

  @action 
  _onLanguageToChange = (language: string) => {
    const previousLanguage = this._languageTo;
    this._languageTo = language;
    localStorage.setItem('languageTo', language);

    if (this._languageFrom === this._languageTo) {
      this._onLanguageFromChange(previousLanguage);
    }

    this._doTranslate();
  };

  @action 
  _onSwapClick = () => {
    const temp = this._languageFrom;
    this._languageFrom = this._languageTo;
    this._languageTo = temp;
    localStorage.setItem('languageFrom', this._languageFrom);
    localStorage.setItem('languageTo', this._languageTo);

    //swap text
    const previousText = this._textFrom;
    this._textFrom = this._translationResult?.translation || '';
    if (this._translationResult) {
      this._translationResult.translation = previousText;
      this._translationResult.isDone = false; // Reset translation status
      this._translationResult.errorMessage = undefined; // Clear any previous error
      localStorage.setItem('translation', JSON.stringify(this._translationResult));
    }

    this._doTranslate();
  };

  @action 
  _onTextFromChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this._textFrom = e.target.value;
    localStorage.setItem('textFrom', this._textFrom);
    this._prefetchModel();
    this._doTranslate();
  }

  @action
  _onCopySourceClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const button = e.currentTarget;
    try {
      await navigator.clipboard.writeText(this._textFrom);
      button.classList.add('copy-success');
      setTimeout(() => {
        button.classList.remove('copy-success');
      }, 1000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  @action
  _onClearClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    this._textFrom = '';
    this._imageBase64 = null;    
    localStorage.removeItem('textFrom');
    localStorage.removeItem('image');
    this._translationResult = null; // Clear previous translation result
    this._explanationResult = null; // Clear explanation result
    this._selectedText = '';
    this._textAreaFromRef.current?.focus(); // Focus the input area
  };

  @action
  _onAttachClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (this._fileRef.current) {
      this._fileRef.current.click();
    }
  };

  @action
  _onImageUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files ? e.target.files[0] : null;

    if (!file || !imageService.validateImageFile(file)) {
      this._fileRef.current!.value = ''; // Clear the input
      return; // No file selected
    }

    try {
      await this._processImageFile(file);
    } catch (error) {
      console.error('Error processing uploaded image:', error);
      alert('Failed to process the uploaded image.');
      this._fileRef.current!.value = ''; // Clear the input
    }
  }

  @action
  _onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const files = await imageService.getFromDataTransfer(clipboardData);
    if (!!files.length){
      e.preventDefault();
      await this._processImageFile(files[0]);
    }
  }
  
  @action
  private _processImageFile = async (file: File): Promise<void> => {
    this._imageBase64 = await imageService.readFileAsBase64(file);
    this._textFrom = ''; // Clear text input when image is uploaded
    localStorage.setItem('textFrom', this._textFrom);
    localStorage.setItem('image', this._imageBase64);
    this._doTranslate();
  };

  @action
  _onCopyTargetClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const button = e.currentTarget;
    try {
      await navigator.clipboard.writeText(this._translationResult?.translation || '');
      button.classList.add('copy-success');
      setTimeout(() => {
        button.classList.remove('copy-success');
      }, 1000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  @action
  _applyCorrection = () => {
    if (this._translationResult && this._translationResult.correctedInput) {
      this._textFrom = this._translationResult.correctedInput;
      this._doTranslate();
    }
  };

  _prefetchModel = helperService.throttle(async () => {
    const currentModel = this._currentModel;
    if (!currentModel) return;

    const activeModels = await apiService.get<LoadedModel[]>('/tags/active');

    const isModelActive = activeModels.some(model => model.name === currentModel.name && model.server === currentModel.server);
    if (isModelActive) return;

    await apiService.post('/tags/prefetch/' + encodeURIComponent(currentModel.name), null, {
      headers: {
        'Content-Type': 'application/json',        
        'x-ollama-server': currentModel.server
      }
    });
  }, 5000);

  private _doTranslate = helperService.debounce(async () => {

    if (this._translationResult?.input === this._textFrom 
      && this._translationResult?.model === this._currentModel?.name
      && this._translationResult?.from === this._languageFrom
      && this._translationResult.isDone) {
      return; // No need to re-translate if the input hasn't changed and translation is done
    }

    await this._callOllamaApi();
  }, 500, false);

  @action.bound
  async _callOllamaApi(): Promise<void> {
    if (!this._textFrom.trim() && !this._imageBase64) {
      this._translationResult = null; // Clear previous result if input is empty
      return;
    }

    const model = this._currentModel;

    const fromLanguage = this._languageFrom;
    const input = this._textFrom.trim();
    this._translationResult = {
      translation: '',
      input: input,
      model: model?.name ?? '',
      from: fromLanguage,
      isDone: false,
    };
    this._explanationResult = null; // Clear explanation when new translation starts
    const result = this._translationResult;

    let resultJson = '';

    const requestUniqueIdToAbort = Math.random().toString(36).substring(2, 15);
    this._abortControllers.forEach((controller, id) => {
      controller.abort();
      this._abortControllers.delete(id);
    });

    const newController = new AbortController();
    this._abortControllers.set(requestUniqueIdToAbort, newController);

    let wakeLock = null;
    //use JS awake api to keep screen on during thinking to avoid connection lost
    if ("wakeLock" in navigator && document.visibilityState === 'visible') {
      try {
        wakeLock = await navigator.wakeLock.request("screen");
      } catch (e) {
        console.warn('Wake lock request failed:', e);
      }
    }

    try {
      
      if (!model) {
        throw new Error('Model not selected, Please select a model to use for translation.');
      }

      const body = {
        from: fromLanguage,
        to: this._languageTo,
        text: input,
        model: model.name,
        image: this._imageBase64
      };
      
      const request = fetch('/api/translate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-loading-progress': 'true',
          'x-ollama-server': model.server ?? ''
        },
        body: JSON.stringify(body),
        signal: newController.signal,
      });
      this._loading.subscribe(request);
      const response = await request;

      if (!response.ok) {
        if (response.status === 429 /* Limit of tokens */) {
          await modalService.showError('Rate limit exceeded', 'You have reached the rate limit. Please try again later.');
          throw new Error('Rate limit exceeded. Please try again later.');
        }

        const errorText = await response.text();
        throw new Error(`Error: ${response.status} - ${errorText}`);
      }
      if (!response.body) {
        throw new Error('No response body received');
      }

      this._refreshActiveModels();
                
      const reader = response.body.getReader();
      
      let buffer = '';

      // Local progress state – accumulated across chunks for this request
      let modelLoadCompleted: number | undefined;
      let modelLoadTotal: number | undefined;
      let promptEvalCompleted: number | undefined;
      let promptEvalTotal: number | undefined;
      let waitingInfo: string | undefined;
      let thinkingTokenCount = 0;

      const applyProgress = () => {
        if (!this._translationResult) return;
        this._translationResult.modelLoadCompleted = modelLoadCompleted;
        this._translationResult.modelLoadTotal = modelLoadTotal;
        this._translationResult.promptEvalCompleted = promptEvalCompleted;
        this._translationResult.promptEvalTotal = promptEvalTotal;
        this._translationResult.waitingInfo = waitingInfo;
        if (thinkingTokenCount > 0) this._translationResult.thinkingTokenCount = thinkingTokenCount;
      };

      const decoder = new TextDecoder();
      while (!newController.signal.aborted) {
        const readerTask = reader.read();
        this._loading.subscribe(readerTask);

        const { done, value } = await readerTask;

        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;

          const json = JSON.parse(line) as OllamaStreamResponse;

          // Capture per-chunk progress fields
          if (json.model_load_completed !== undefined) modelLoadCompleted = json.model_load_completed;
          if (json.model_load_total !== undefined) modelLoadTotal = json.model_load_total;
          if (json.prompt_eval_completed !== undefined) promptEvalCompleted = json.prompt_eval_completed;
          if (json.prompt_eval_total !== undefined) promptEvalTotal = json.prompt_eval_total;
          if (json.waiting_info !== undefined) waitingInfo = json.waiting_info;
          if (json.message?.thinking) thinkingTokenCount += 1;

          resultJson += json.message?.content || '';
          this._processResultJson(resultJson, body.text, model.name, fromLanguage, json.done || false);
          runInAction(applyProgress);
        }
      }

      if (buffer.trim()) {
        const json = JSON.parse(buffer) as OllamaStreamResponse;
        if (json.model_load_completed !== undefined) modelLoadCompleted = json.model_load_completed;
        if (json.model_load_total !== undefined) modelLoadTotal = json.model_load_total;
        if (json.prompt_eval_completed !== undefined) promptEvalCompleted = json.prompt_eval_completed;
        if (json.prompt_eval_total !== undefined) promptEvalTotal = json.prompt_eval_total;
        if (json.waiting_info !== undefined) waitingInfo = json.waiting_info;
        if (json.message?.thinking) thinkingTokenCount += 1;
        resultJson += json.message?.content || '';
      }
      this._processResultJson(resultJson, body.text, model.name, fromLanguage, true);
      runInAction(applyProgress);
      
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      const errorName = e instanceof Error ? e.name : 'Error';

      runInAction(() => {
        result.errorMessage = `${errorName === 'Error' ? '' : errorName} ${errorMessage}`.trim();
        result.isDone = true;
      });

    } finally {
      this._abortControllers.delete(requestUniqueIdToAbort);
      localStorage.setItem('translation', JSON.stringify(this._translationResult));
      wakeLock?.release();
    }

  }

  @action
  private _processResultJson = (resultJson: string, input: string, modelName: string, fromLanguage: string, isDone: boolean) => {
    if (input.trim() !== this._textFrom.trim()) {
      console.warn('Input text has changed during translation, ignoring result');
      return;
    }

    const resultTranslation = helperService.closeIncompleteJson<TranslationResult>(resultJson);
    if (resultTranslation){
      this._translationResult = resultTranslation;
      this._translationResult.model = modelName;
      this._translationResult.input = input;
      this._translationResult.from = fromLanguage;
      this._translationResult.isDone = isDone;
    }
    
    if (this._translationResult){
      this._translationResult.isDone = isDone;
      localStorage.setItem('translation', JSON.stringify(this._translationResult));
    }
  }

  private _refreshActiveModels = () => {
    this._modelSelectorRef.current?.loadActiveModels();
  }

  @action
  private _onTextSelect = () => {
    const textarea = this._textAreaFromRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = start !== end ? textarea.value.substring(start, end).trim() : '';
    this._selectedText = selected;
  };

  @action
  _onExplainClick = () => {
    if (!this._selectedText) return;
    void this._callExplainApi(this._selectedText);
  };

  @action
  _onExplainKeyword = (word: string) => {
    void this._callExplainApi(word);
  };

  @action.bound
  async _callExplainApi(selection: string): Promise<void> {
    const model = this._currentModel;
    if (!model || !selection.trim()) return;

    // abort any ongoing explain request
    this._abortControllers.forEach((controller, id) => {
      if (id.startsWith('explain-')) {
        controller.abort();
        this._abortControllers.delete(id);
      }
    });

    const explainCtrlId = `explain-${Math.random().toString(36).substring(2, 15)}`;
    const explainCtrl = new AbortController();
    this._abortControllers.set(explainCtrlId, explainCtrl);

    this._explanationResult = {
      isDone: false,
      selection: selection.trim(),
    };
    const resultRef = this._explanationResult;

    try {
      const body: ExplainRequest = {
        from: this._languageFrom,
        to: this._languageTo,
        text: this._textFrom.trim(),
        selection: selection.trim(),
        model: model.name,
        image: this._imageBase64 || null,
      };

      const response = await fetch('/api/translate/explain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-ollama-server': model.server ?? '',
        },
        body: JSON.stringify(body),
        signal: explainCtrl.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error: ${response.status} - ${errorText}`);
      }

      if (!response.body) throw new Error('No response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let resultJson = '';

      while (!explainCtrl.signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const json = JSON.parse(line) as OllamaStreamResponse;
          const chunk = json.message?.content || '';
          resultJson += chunk;
          const parsed = helperService.closeIncompleteJson<ExplanationResult>(resultJson);
          runInAction(() => {
            resultRef.tokenCount = (resultRef.tokenCount ?? 0) + (chunk ? 1 : 0);
            if (parsed) {
              resultRef.explanation = parsed.explanation;
              resultRef.examples = parsed.examples;
              resultRef.comments = parsed.comments;
            }
          });
        }
      }

      if (buffer.trim()) {
        const json = JSON.parse(buffer) as OllamaStreamResponse;
        resultJson += json.message?.content || '';
      }
      const finalParsed = helperService.closeIncompleteJson<ExplanationResult>(resultJson);
      runInAction(() => {
        if (finalParsed) {
          resultRef.explanation = finalParsed.explanation;
          resultRef.examples = finalParsed.examples;
          resultRef.comments = finalParsed.comments;
        }
        resultRef.isDone = true;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      const name = e instanceof Error ? e.name : 'Error';
      runInAction(() => {
        resultRef.errorMessage = `${name === 'Error' ? '' : name} ${msg}`.trim();
        resultRef.isDone = true;
      });
    } finally {
      this._abortControllers.delete(explainCtrlId);
    }
  }

  @action.bound
  private _onTranscription = (text: string) => {
    this._textFrom = (this._textFrom ? ' ' : '') + text;
    localStorage.setItem('textFrom', this._textFrom);
    this._doTranslate();
  };

  private static _formatBytes(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  }

  private _renderLoadingProgress(): React.ReactNode {
    const result = this._translationResult;
    if (!result || result.isDone) return null;

    const hasWaitingInfo = !!result.waitingInfo;
    const hasModelLoadProgress = !!result.modelLoadTotal && result.modelLoadTotal > 0;
    const hasPromptEvalProgress = !!result.promptEvalTotal && result.promptEvalTotal > 0;

    if (!hasWaitingInfo && !hasModelLoadProgress && !hasPromptEvalProgress) return null;

    const modelLoadPercent = hasModelLoadProgress
      ? Math.min(100, (result.modelLoadCompleted || 0) / result.modelLoadTotal! * 100)
      : 0;

    const promptEvalPercent = hasPromptEvalProgress
      ? Math.min(100, (result.promptEvalCompleted || 0) / result.promptEvalTotal! * 100)
      : 0;

    const hasContent = !!result.translation;
    const isWaitingForWorker = hasWaitingInfo && !hasModelLoadProgress && !hasContent;
    const isModelLoading = hasModelLoadProgress && modelLoadPercent < 100 && !hasContent;
    const isPromptEval = !isModelLoading && hasPromptEvalProgress && promptEvalPercent < 100 && !hasContent;
    const isTakingLonger = !isModelLoading && !isPromptEval && hasPromptEvalProgress && promptEvalPercent >= 100 && !hasContent;

    if (!isWaitingForWorker && !isModelLoading && !isPromptEval && !isTakingLonger) return null;

    const fmt = Translate._formatBytes;

    return (
      <div className="model-loading-progress">
        {isWaitingForWorker && (
          <div className="progress-item waiting-for-worker">
            <div className="progress-label">
              <span className="longer-spinner"></span>
              {result.waitingInfo}
            </div>
          </div>
        )}
        {isModelLoading && (
          <div className="progress-item">
            <div className="progress-label">
              Loading model: {fmt(result.modelLoadCompleted || 0)} / {fmt(result.modelLoadTotal!)}
            </div>
            <div className="progress-bar">
              <div className="progress-bar-fill progress-bar-blue" style={{ width: `${modelLoadPercent}%` }}></div>
            </div>
          </div>
        )}
        {isPromptEval && (
          <div className="progress-item">
            <div className="progress-label">
              Reading context: {result.promptEvalCompleted || 0} / {result.promptEvalTotal!} tokens
            </div>
            <div className="progress-bar">
              <div className="progress-bar-fill progress-bar-green" style={{ width: `${promptEvalPercent}%` }}></div>
            </div>
          </div>
        )}
        {isTakingLonger && (
          <div className="progress-item taking-longer">
            <div className="progress-label">
              <span className="longer-spinner"></span>
              Takes longer than expected, please wait...
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default Translate;